import useReport from "@/hooks/useReport";
import { Menu } from "@headlessui/react";
import classnames from "classnames";
import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from "react";
import { createPortal } from "react-dom";
import { Modal, ModalContent, ModalDescription, ModalHeading, InputField, DynamicHeroIcon, ButtonSolid, } from "@intrasys/core-ui";
  
  export interface PageOptions {
    print?: boolean;
    maxPage: number;
  }
  export type pageOptionsData = {
    pages: "all" | "custom",
    pageNumber?: string[],
    proceed: boolean
  }
  export type PageOptionState = {
    id: string;
  } & PageOptions;
  
  export type PageOptionContextProps = (
    opt: PageOptions
  ) => Promise<pageOptionsData>;
  
  export const PageOptionContext =
    createContext<PageOptionContextProps | null>(null);
  
  export interface PageOptionProviderProps {
    children: ReactNode;
  }
  
  export const usePageOption = () => useContext(PageOptionContext);
  
  type PageOptionResolver = (state: PromiseLike<pageOptionsData> | pageOptionsData) => void;

  export const PageOptionProvider = ({
    children
  }: PageOptionProviderProps) => {
    const resolver = useRef<PageOptionResolver>();
    const [print, setPrint] = useState(false);
    const [maxPage, setMaxPage] = useState<number>(10);
    const {showPageOptions, updateShowPageOptions} =
    useReport((state) => ({
      showPageOptions: state.showPageOptions,
      updateShowPageOptions: state.updateShowPageOptions,
    }));
    const confirm = (opt: PageOptions) => {
      updateShowPageOptions(true);
      setPrint(opt.print?? false);
      setMaxPage(opt.maxPage);
      return new Promise<pageOptionsData>(function (resolve) {
        resolver.current = resolve;
      });
    };
    const handleConfirm = (data: pageOptionsData) => {
      resolver && resolver.current && resolver.current(data);
      // setModalState(false);
    };
  
    const handleCancel = () => {
      resolver && resolver.current && resolver.current({
        pages: "all",
        pageNumber:[],
        proceed: false,
      });
      updateShowPageOptions(false)
    };
  
    const contextValue = React.useMemo(() => confirm, []);
    return (
      <PageOptionContext.Provider value={contextValue}>
        {children}
        {createPortal(
          <>
            <PageOptionModal
              maxPage={maxPage}
              print={print}
              cancelCallback={handleCancel}
              confirmationCallback={handleConfirm}
              open={showPageOptions}
              onOpenChange={updateShowPageOptions}
            />
          </>,
          document.body
        )}
      </PageOptionContext.Provider>
    );
  };
  
  interface PageOptionModalProps extends PageOptions {
    confirmationCallback: (data: pageOptionsData) => void;
    cancelCallback: () => void;
    open: boolean;
    onOpenChange: (state: boolean) => void;
  }
  
  const PageOptionModal = ({
    confirmationCallback,
    cancelCallback,
    print,
    open,
    onOpenChange,
    maxPage,
  }: PageOptionModalProps) => {
    const [pagesToPrint, setPageToPrint] = useState<string[]>([]);
    const [errorMessage, setErrorMessage] = useState("");
    const pages = [{name: 'Custom Pages', value: "custom"}, {name: 'All Pages', value: "all" }]
    const [selected, setSelected] = useState(pages[0])
    const {isLoading} =
    useReport((state) => ({
      isLoading: state.isLoading,
    }));
    const handleOnChange = useCallback((value:string) => {
        setPageToPrint([]);
        setErrorMessage("");
        let tempPageData:string[] = [];
        if(value.match(/[A-Za-z]|[^A-Za-z0-9-,]|[-,]$|-[,-0]|,[,-0]|-\d*-|^0/g)  !== null || value.length ===0){
            setErrorMessage("Invalid input");
            return;
           }
        let pages = value.split(",")
        pages.map((item) => {
          if(item.includes("-")){
            let range = item.split("-");
            for(let i=parseInt(range[0]); i <= parseInt(range[1]); i++){
              if(!tempPageData.includes(i.toString())){
                if(i > maxPage){
                  setErrorMessage("Page number exceeded");
                  return;
                }
                tempPageData.push(i.toString());
              }
            }
            
          }
          else{
            if(!tempPageData.includes(item)){
              if(+item > maxPage){
                setErrorMessage("Page number exceeded");
                return;
              }
              tempPageData.push(item);
            }  
          }
        })
        tempPageData.sort();
        if(tempPageData.length > 100){
          setErrorMessage("Maximum limit of 100 pages exceeded.");
          return;
        } 
        setPageToPrint(tempPageData??undefined);
    },[pagesToPrint, maxPage]);

    useEffect(() => {
      if(selected.value === "all" && maxPage >= 100){
        setErrorMessage("Maximum limit of 100 pages exceeded.");
      }
    },[selected]);

    useEffect(() => {
      setSelected(pages[0]);
      setErrorMessage("");
    },[open])
    return (
        <Modal open={open} onOpenChange={onOpenChange}>
        <ModalContent className="  mt-10 rounded-lg bg-white shadow dark:bg-gray-900  dark:border-gray-700 w-80">
            <ModalHeading showClose={true}>
                {print? "Print" : "Export"}
            </ModalHeading>
            <ModalDescription>
            <div className="flex flex-row items-center gap-4 min-w-[200px]">
                <p className="dark:text-white">Pages: </p>
                <Menu
                            as={"div"}
                            className="rounded-lg border shadow dark:border-gray-700 dark:bg-gray-800"
                          >
                            <Menu.Button className=" flex flex-row px-4 py-2 min-w-[200px] align-middle justify-between items-center text-center rounded bg-gray-40 text-gray-600 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-700">
                                {selected.name}
                                <DynamicHeroIcon
                                        icon="ChevronDownIcon"
                                        className="h-4 w-4"
                                      />
                            </Menu.Button>
                            <Menu.Items className="absolute z-50 w-40 max-w-xs  min-w-[200px] flex flex-col rounded-lg mt-1 border border-gray-100 shadow-md bg-white text-gray-600 p-1 text-sm focus:outline-none dark:divide-gray-800 dark:bg-gray-900 dark:scrollbar-track-gray-600 dark:border-gray-700">
                                  {pages.map((item, index) =>(
                                    <Menu.Item key={index}>
                                  {({ active }) => (
                                    <button
                                      className={classnames(
                                        "focuse:outline-none inline-flex items-center gap-1 rounded p-2 text-left  hover:bg-gray-200  dark:hover:bg-gray-800 dark:hover:text-white",
                                        {
                                          "bg-gray-100 text-gray-900 dark:bg-gray-600 dark:text-white":
                                            active,
                                        },
                                        {
                                          "text-gray-600 dark:text-gray-500":
                                            !active,
                                        }
                                      )}
                                      onClick={() => {
                                                        setSelected(item);
                                                        setPageToPrint([]);
                                                        setErrorMessage("");
                                                    }}
                                    >
                                    <div className="">
                                      {selected.name === item.name? 
                                          <DynamicHeroIcon
                                        icon="CheckIcon"
                                        type="outline"
                                        className="h-4 w-4 text-primary-400 font-bold"
                                      />
                                      :<p className="mx-2"></p>}
                                    </div>
                                      <p className="ml-2">{item.name}</p>
                                    </button>
                                  )}
                                </Menu.Item>
                                  ))}
                            </Menu.Items>
                          </Menu>
            </div>
            
            <div className="min-w-[200px]">
            {selected.value === "custom" ? 
                          <InputField 
                            placeholder="e.g. 1-3,5,7-9"
                            error={errorMessage}
                            onChange={(e) => handleOnChange(e.currentTarget.value)}/>
                          : errorMessage ? 
                          <p className="mt-2 text-sm text-red-600 dark:text-red-500">{errorMessage}</p>
                          : null}
            </div>
            </ModalDescription>
            <div className="flex flex-row gap-2 justify-center p-2">
                <ButtonSolid label="Cancel" variant="alternative" onClick={cancelCallback}/>
                <ButtonSolid 
                  isLoading={isLoading}
                  label="Submit" 
                  disabled={!errorMessage &&
                             pagesToPrint &&
                             pagesToPrint.length !==0 ||
                             (selected.value === "all" && !errorMessage)? false : true}
                  onClick={() => confirmationCallback({
                    pages: selected.value === "all" ? "all" : "custom",
                    pageNumber: pagesToPrint,
                    proceed: true
                  })}/>
            </div>
        </ModalContent>
    </Modal>
    );
  };
  
  export default PageOptionModal;
  